import React from "react";

import logger from "js/core/logger";
import { isTesting } from "js/config";

const isElectron = navigator.userAgent.includes("Electron/");

const GoogleAuthContext = React.createContext();
export const GoogleAuthConsumer = GoogleAuthContext.Consumer;

const log = logger.info(logger.AUTH).bind(logger);

/**
 * Provider for Google Sign-in `gapi.auth2` object.
 */
export class GoogleAuthProvider extends React.Component {
    constructor(props) {
        super(props);
        this.state = { didInit: false };
        this.auth2 = React.createRef(null);
    }

    async componentDidMount() {
        if (!window.gapi) {
            // Google platform script did not load
            console.error("[auth] Google platform failed to load");
            window.gapi = { auth2: {} };
            this.fakeAuth();
            this.setState({ didInit: true });
            return;
        }

        log("Waiting for Google Sign-In to load");

        let didLoad = false;
        let didTimeOut = false;

        setTimeout(() => {
            if (!didLoad) {
                didTimeOut = true;
                console.error("[auth] Timed-out waiting for Google Sign-In to load");
                window.gapi = { auth2: {} };
                this.fakeAuth();
                this.setState({ didInit: true });
                return;
            }
        }, 15000);

        window.gapi.load("auth2", async () => {
            if (didTimeOut) {
                return;
            }
            didLoad = true;

            log("Google Sign-In loaded, waiting to initalize");

            // Google Sign-in doesn't work offline, fake it for Electron
            if (isElectron && !navigator.onLine) {
                log("Skipped Google Sign-In initalize for offline Electron");
                this.fakeAuth();
                this.setState({ didInit: true });
                return;
            }

            // note: init resumes the session of any user who previously signed-in
            let auth2;
            try {
                auth2 = await window.gapi.auth2.init({
                    client_id: window.baiFirebase.client_id,
                    scope: "profile email",
                    cookie_policy: "single_host_origin",
                    prompt: "select_account",
                    ux_mode: "popup"
                });
            } catch (err) {
                if (!isTesting) {
                    console.error("[auth] Google Sign-In failed to initalize");
                    console.error(err);
                }
                this.fakeAuth(err);
                this.setState({ didInit: true });
                return;
            }

            this.auth2.current = auth2;
            window.auth2 = auth2; // for debugging

            const isSignedIn = auth2.isSignedIn.get();
            const user = auth2.currentUser.get();
            log("Google Sign-In initalized, isSignedIn =", isSignedIn);
            if (isSignedIn) {
                log("Google Sign-In user =", user);
            }

            this.setState({ didInit: true });
        });
    }

    fakeAuth(error) {
        // fake auth for Electron offline
        const fakeAuth2 = {
            isSignedIn: {
                get() {
                    return false;
                }
            },
            error
        };
        this.auth2.current = fakeAuth2;
        window.gapi.auth2.getAuthInstance = function() {
            return fakeAuth2;
        };
    }

    render() {
        const { children } = this.props;
        return (
            <GoogleAuthContext.Provider
                value={this.state.didInit ? this.auth2.current : null}
            >
                {children}
            </GoogleAuthContext.Provider>
        );
    }
}

/**
 * Workaround for ad-hoc ReactDOM.render trees within Backbone views,
 * which are unable to access the context tree. You *must* ensure that
 * there's an initalized GoogleAuthProvider somewhere above this.
 */
export function UNSAFE_GoogleAuthProvider({ children }) {
    return (
        <GoogleAuthContext.Provider value={window.gapi.auth2.getAuthInstance()}>
            { children }
        </GoogleAuthContext.Provider>
    );
}

/**
 * HOC which injects `GoogleAuth` instance as `auth2` prop. Very rarely needed!
 */
export function withGoogleAuth(Component) {
    return function WithGoogleAuth(props) {
        return (
            <GoogleAuthContext.Consumer>
                {auth2 => <Component {...props} auth2={auth2} />}
            </GoogleAuthContext.Consumer>
        );
    };
}
